/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ評価一覧ページ。フィルタ付きテーブルでバグi-point評価の一覧を表示し、詳細ページへ遷移。確定権限があれば一次判定が見送った課題も出す
 * @graph-connects useBugEvaluations [calls] バグ評価一覧フック
 * @graph-connects useBugEvaluationAiDeclines [calls] 一次判定が見送った課題のフック
 * @graph-connects AiDeclinedCard [calls] 見送られた課題の描画
 * @graph-connects ProtectedRoute [calls] VIEW_BUG_EVALUATION 権限でアクセスをガードする
 */
import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { ProtectedRoute } from '../../components/auth/index.js';
import { AiDeclinedCard } from '../../components/features/bug-evaluation/AiDeclinedCard';
import { REVIEW_ROLES } from '../../components/features/bug-evaluation/constants';
import { useAuth } from '../../hooks/useAuth';
import { useBugEvaluationAiDeclines, useBugEvaluations } from '../../hooks/useBugEvaluation';
import { computePeriodRange } from '../../lib/period';
import { TEAM_OPTIONS } from '@cortex/domain-product-innovation-lab-kpi-team-filter';

/** @graph-connects none */
export const Route = createFileRoute('/bug-evaluation/')({
  component: () => (
    <ProtectedRoute pageKey="bugEvaluation">
      <BugEvaluationListPage />
    </ProtectedRoute>
  ),
});

/** @graph-connects none */
function getScoreColor(score: number): string {
  if (score >= 8) return 'text-red-600 font-bold';
  if (score >= 5) return 'text-yellow-600 font-semibold';
  return 'text-green-600';
}

/** @graph-connects none */
const PERIOD_OPTIONS = [
  { value: '30', label: '30' },
  { value: '90', label: '90' },
  { value: '180', label: '180' },
  { value: '365', label: '365' },
  { value: 'all', label: '' },
] as const;

/** @graph-connects useBugEvaluations [calls] バグ評価一覧フック */
function BugEvaluationListPage() {
  const { t } = useTranslation();
  const defaults = computePeriodRange('90');
  const [reviewStatus, setReviewStatus] = useState<string>('unreviewed');
  const [team, setTeam] = useState<string>('');
  const [period, setPeriod] = useState<string>('90');
  const [startDate, setStartDate] = useState<string>(defaults.startDate ?? '');
  const [endDate, setEndDate] = useState<string>(defaults.endDate ?? '');

  const handlePeriodChange = (value: string) => {
    setPeriod(value);
    const range = computePeriodRange(value);
    setStartDate(range.startDate ?? '');
    setEndDate(range.endDate ?? '');
  };

  // 見送りの理由文は判定の内情なので、確定できる権限のときだけ引く（API 側も同じ 3 ロール）
  const { user } = useAuth();
  const canReview = (user?.portalRoles ?? []).some((role) =>
    REVIEW_ROLES.includes(role as (typeof REVIEW_ROLES)[number]),
  );
  const { data: declines } = useBugEvaluationAiDeclines(canReview);

  const { data, isLoading } = useBugEvaluations({
    reviewStatus,
    team: team || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ページヘッダー */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('nav.bugEvaluation', 'バグ評価')}
        </h1>
        <p className="mt-1 text-[var(--color-text-secondary)]">
          {t('bugEvaluation.listDescription', 'Geminiによるi-point評価結果の確認・レビュー')}
        </p>
      </div>

      {/* 一次判定が見送った課題。**評価一覧には出てこない**ので、ここが唯一の入口になる */}
      <AiDeclinedCard declines={declines?.data ?? []} />

      {/* フィルタ */}
      <Card className="p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
              {t('bugEvaluation.period', '期間')}
            </label>
            <select
              value={period}
              onChange={(e) => {
                handlePeriodChange(e.target.value);
              }}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm"
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.value === 'all'
                    ? t('bugEvaluation.filterAll', 'すべて')
                    : t(
                        'time.last' + opt.label + 'Days',
                        opt.label + t('bugEvaluation.days', '日'),
                      )}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
              {t('bugEvaluation.team', 'チーム')}
            </label>
            <select
              value={team}
              onChange={(e) => {
                setTeam(e.target.value);
              }}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm"
            >
              <option value="">{t('bugEvaluation.filterAll', 'すべて')}</option>
              {TEAM_OPTIONS.filter((o) => o.value !== 'all').map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
              {t('bugEvaluation.reviewStatus', 'レビュー状態')}
            </label>
            <select
              value={reviewStatus}
              onChange={(e) => {
                setReviewStatus(e.target.value);
              }}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm"
            >
              <option value="all">{t('bugEvaluation.filterAll', 'すべて')}</option>
              <option value="unreviewed">{t('bugEvaluation.unreviewed', '未レビュー')}</option>
              <option value="reviewed">{t('bugEvaluation.reviewed', 'レビュー済み')}</option>
            </select>
          </div>
          <div className="text-sm text-[var(--color-text-muted)]">
            {data ? `${String(data.total)}${t('bugEvaluation.items', '件')}` : ''}
          </div>
        </div>
      </Card>

      {/* テーブル（デスクトップ） */}
      <Card className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                {t('bugEvaluation.issueKey', '課題キー')}
              </th>
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                {t('bugEvaluation.summary', 'サマリー')}
              </th>
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                {t('bugEvaluation.team', 'チーム')}
              </th>
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                {t('bugEvaluation.bugType', '種別')}
              </th>
              <th className="px-4 py-3 text-center font-medium text-[var(--color-text-secondary)]">
                i-point
              </th>
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                {t('bugEvaluation.negligenceLevel', '過失レベル')}
              </th>
              <th className="px-4 py-3 text-center font-medium text-[var(--color-text-secondary)]">
                {t('bugEvaluation.review', 'レビュー')}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              [1, 2, 3].map((i) => (
                <tr key={i} className="border-b border-[var(--color-border)]">
                  <td className="px-4 py-3">
                    <div className="h-4 w-20 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 w-48 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 w-16 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 w-8 mx-auto rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 w-16 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-5 w-8 mx-auto rounded-full bg-[var(--color-bg-elevated)] animate-pulse" />
                  </td>
                </tr>
              ))
            ) : data?.data.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-[var(--color-text-muted)]">
                  {t('status.noData', 'データがありません')}
                </td>
              </tr>
            ) : (
              data?.data.map((item) => (
                <tr
                  key={item.issueKey}
                  className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]"
                >
                  <td className="px-4 py-3">
                    <Link
                      to="/bug-evaluation/$issueKey"
                      params={{ issueKey: item.issueKey }}
                      className="text-[var(--color-accent-primary)] hover:underline font-mono text-xs"
                    >
                      {item.issueKey}
                    </Link>
                  </td>
                  <td className="px-4 py-3 max-w-xs truncate">{item.summary}</td>
                  <td className="px-4 py-3 text-xs">
                    {item.devLeaderTeam || item.responsibleTeam}
                  </td>
                  <td className="px-4 py-3 text-xs">{item.bugType}</td>
                  <td className={`px-4 py-3 text-center ${getScoreColor(item.iPoint)}`}>
                    {item.iPoint}
                  </td>
                  <td className="px-4 py-3 text-xs">{item.negligenceLevel}</td>
                  <td className="px-4 py-3 text-center">
                    {item.isHumanReviewed ? (
                      <span className="inline-block rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                        {t('bugEvaluation.done', '済')}
                      </span>
                    ) : (
                      <span className="inline-block rounded-full bg-yellow-100 px-2 py-0.5 text-xs text-yellow-700">
                        {t('bugEvaluation.pending', '未')}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {/* カードビュー（モバイル） */}
      <div className="block md:hidden space-y-3">
        {isLoading
          ? [1, 2, 3].map((i) => (
              <Card key={i} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="h-4 w-20 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                  <div className="h-6 w-8 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                </div>
                <div className="h-4 w-3/4 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                <div className="h-3 w-1/2 mt-2 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
              </Card>
            ))
          : data?.data.map((item) => (
              <Link
                key={item.issueKey}
                to="/bug-evaluation/$issueKey"
                params={{ issueKey: item.issueKey }}
              >
                <Card className="p-4 hover:bg-[var(--color-bg-hover)]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-xs text-[var(--color-accent-primary)]">
                      {item.issueKey}
                    </span>
                    <span className={`text-lg ${getScoreColor(item.iPoint)}`}>{item.iPoint}</span>
                  </div>
                  <div className="text-sm truncate">{item.summary}</div>
                  <div className="mt-1 text-xs text-[var(--color-text-secondary)]">
                    {(item.devLeaderTeam || item.responsibleTeam) && (
                      <span>{item.devLeaderTeam || item.responsibleTeam} / </span>
                    )}
                    {item.bugType}
                  </div>
                  <div className="mt-1 text-xs text-[var(--color-text-muted)]">
                    {item.negligenceLevel} ・{' '}
                    {item.isHumanReviewed
                      ? t('bugEvaluation.reviewed', 'レビュー済')
                      : t('bugEvaluation.unreviewed', '未レビュー')}
                  </div>
                </Card>
              </Link>
            ))}
      </div>
    </div>
  );
}
